import { currencyFormat } from "@/utilities/functions";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useOnboardingStore = defineStore("onboardingStore", () => {
  //1st page
  const email = ref("");
  const brandIndustry = ref("");
  const brandName = ref("");
  const brandCountry = ref("");
  const brandTimeZone = ref("");
  const brandCurrency = ref("");
  const brandAverageSpend = ref(0);
  const storeLocation = ref("");
  const storeTimeZone = ref("");

  //average transaction tab
  const customerSpendMode = ref("per transaction");
  const customerSpend = ref("");
  const loyaltyProgramType = ref("points");

  //stpc tab
  const brandEarningSelection = ref("");
  const brandSpending = ref(1);
  const brandPoints = ref(1);

  //rewards tab
  const tempbrandRewards = ref([]);
  const rewards = ref([]);
  const rewardCounter = ref(0);

  const row1Deal1 = ref(true);
  const row1Deal2 = ref(false);
  const row2Deal1 = ref(true);
  const row2Deal2 = ref(false);
  const row3Deal1 = ref(true);
  const row3Deal2 = ref(false);

  //default parameters
  const t1ValueOfRedemption = ref("");
  const t2ValueOfRedemption = ref("");
  const t3ValueOfRedemption = ref("");
  const t1NoOfVisits = ref("");
  const t2NoOfVisits = ref("");
  const t3NoOfVisits = ref("");
  const brandAOVMultiplier = ref(null);
  const defaultbrandAOVMultiplier = ref(null);

  //page navigation
  const page = ref("details");
  const previousPage = ref(null);

  //stpc
  const stpcThreshold = ref(1);
  const stpcModifier = ref(1);
  const stpcSpecification = ref("");

  //registration draft information
  const draftId = ref("");
  const verificationCode = ref("");

  function makeDeal(noOfVisit, valueOfRedemption) {
    const amount =
      valueOfRedemption *
      customerSpend.value *
      noOfVisit *
      defaultbrandAOVMultiplier.value;
    const points =
      (noOfVisit * customerSpend.value * defaultbrandAOVMultiplier.value) /
      (brandSpending.value / brandPoints.value);

    return {
      currency: `${brandCurrency.value} ${currencyFormat(roundAmount(amount))}`,
      point: currencyFormat(roundAmount(points)),
      monetaryCost: currencyFormat(roundAmount(amount)),
    };
  }

  const firstDeal = computed(() => {
    return makeDeal(t1NoOfVisits.value, t1ValueOfRedemption.value);
  });
  const secondDeal = computed(() => {
    return makeDeal(t2NoOfVisits.value, t2ValueOfRedemption.value);
  });
  const thirdDeal = computed(() => {
    return makeDeal(t3NoOfVisits.value, t3ValueOfRedemption.value);
  });

  const defaultRewards = computed(() => {
    const rewardTypeKeys = ["free_item", "cash_discount"];
    let deals;

    if (loyaltyProgramType.value === "points") {
      deals = [firstDeal, secondDeal, thirdDeal];
    } else {
      deals = [firstDeal];
    }

    const rewardValue = (deal) =>
      loyaltyProgramType.value === "points" ? deal.value.point : 8;

    return deals.flatMap((deal) =>
      rewardTypeKeys.map((type) => ({
        type,
        title:
          type === "free_item"
            ? `1 Free Item Worth ${deal.value.currency}`
            : `${deal.value.currency} Cash Rebate`,
        active: type === "free_item",
        points: rewardValue(deal),
        monetaryCost: deal.value.monetaryCost,
        discountLimit: 0,
      }))
    );
  });

  const isRewardValid = computed(() => {
    if (!rewards.value || rewards.value.length === 0) return false;

    return rewards.value.every((reward) => {
      return (
        reward.type &&
        reward.title &&
        parseInt(reward.points) > 0 &&
        parseInt(reward.monetaryCost) > 0
      );
    });
  });

  function resetState() {
    brandAOVMultiplier.value = "";
    defaultbrandAOVMultiplier.value = "";
    brandName.value = "";
    email.value = "";
    brandCountry.value = "";
    brandTimeZone.value = "";
    brandIndustry.value = "";
    brandCurrency.value = "";
    brandAverageSpend.value = 0;
    brandEarningSelection.value = "";
    brandSpending.value = 1;
    brandPoints.value = 1;
    tempbrandRewards.value = [];
    rewards.value = [];
    rewardCounter.value = 0;
    customerSpendMode.value = "per transaction";
    customerSpend.value = "";
    storeLocation.value = "";
    storeTimeZone.value = "";

    t1ValueOfRedemption.value = "";
    t2ValueOfRedemption.value = "";
    t3ValueOfRedemption.value = "";
    t1NoOfVisits.value = "";
    t2NoOfVisits.value = "";
    t3NoOfVisits.value = "";

    row1Deal1.value = true;
    row1Deal2.value = false;
    row2Deal1.value = true;
    row2Deal2.value = false;
    row3Deal1.value = true;
    row3Deal2.value = false;

    // page.value = "customer-spend"
    page.value = "details";
    stpcThreshold.value = 1;
    stpcSpecification.value = "";
    stpcModifier.value = 1;

    // Reset draft information
    draftId.value = "";
    verificationCode.value = "";
  }

  return {
    brandAOVMultiplier,
    defaultbrandAOVMultiplier,
    brandName,
    brandCountry,
    brandTimeZone,
    brandIndustry,
    brandCurrency,
    brandAverageSpend,
    brandEarningSelection,
    brandSpending,
    brandPoints,
    tempbrandRewards,
    rewards,
    rewardCounter,
    page,
    previousPage,
    email,
    customerSpendMode,
    customerSpend,
    t1ValueOfRedemption,
    t2ValueOfRedemption,
    t3ValueOfRedemption,
    t1NoOfVisits,
    t2NoOfVisits,
    t3NoOfVisits,
    row1Deal1,
    row1Deal2,
    row2Deal1,
    row2Deal2,
    row3Deal1,
    row3Deal2,
    storeLocation,
    storeTimeZone,
    stpcThreshold,
    stpcSpecification,
    stpcModifier,
    draftId,
    verificationCode,
    resetState,
    defaultRewards,
    firstDeal,
    secondDeal,
    thirdDeal,
    makeDeal,
    loyaltyProgramType,
    isRewardValid,
  };
});

export const useOnboardingModalsStore = defineStore(
  "onboardingModalsStore",
  () => {
    const onboardingModal = ref(null);
    const rewardsStampCardModal = ref(null);
    const activateModal = ref(null);

    return {
      onboardingModal,
      rewardsStampCardModal,
      activateModal,
    };
  }
);

function decimalPointLarge(value) {
  let decimalPart = value - Math.floor(value);

  return decimalPart >= 0.01;
}

function roundAmount(value) {
  return decimalPointLarge(value) ? Math.ceil(value) : Math.floor(value);
}

export const promotionType = {
  percentage_discount: "Percentage Discount",
  free_shipping: "Free Shipping",
  free_item: "Free Item",
  cash_discount: "Cash Rebate",
};
